import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Modal, 
  FormControlLabel, 
  Switch, 
  Tooltip,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCalculator } from '../../../contexts/CalculatorContext';

/**
 * Modal component for AI Conversation Intelligence settings
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {boolean} props.nonEnglishAnalysis - Whether non-English analysis is enabled
 * @param {Function} props.onApply - Function to apply settings
 */
const AISettingsModal = ({ 
  open, 
  onClose, 
  nonEnglishAnalysis,
  onApply 
}) => {
  // Локальний стан для налаштувань
  const [localNonEnglishAnalysis, setLocalNonEnglishAnalysis] = React.useState(nonEnglishAnalysis);
  const theme = useTheme(); // Hook to get theme
  const { t } = useTranslation(); // Hook for translations
  
  const {
    veryCustomProductDiscounts,
    handleUpdateVeryCustomProductDiscount
  } = useCalculator();
  
  // Advanced Settings state
  const currentDiscount = veryCustomProductDiscounts['ai_conversation'] || 0;
  const [isAdvancedMode, setIsAdvancedMode] = React.useState(currentDiscount > 0);
  const [localDiscount, setLocalDiscount] = React.useState(currentDiscount);
  const [discountString, setDiscountString] = React.useState(String(currentDiscount))
  
  // Оновлення локальних станів при зміні пропсів
  React.useEffect(() => {
    setLocalNonEnglishAnalysis(nonEnglishAnalysis);
    const currentDiscount = veryCustomProductDiscounts['ai_conversation'] || 0;
    setLocalDiscount(currentDiscount);
    setDiscountString(String(currentDiscount));
    setIsAdvancedMode(currentDiscount > 0);
  }, [nonEnglishAnalysis, open, veryCustomProductDiscounts]);
  
  // Handle keyboard shortcuts for advanced mode
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl+Shift+C (PC) or Cmd+Shift+C (Mac)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'C') {
        event.preventDefault();
        setIsAdvancedMode(prev => !prev);
      }
    };
    
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }
    
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);
  
  // Handle double click on title for advanced mode
  const handleTitleDoubleClick = () => {
    setIsAdvancedMode(prev => !prev);
  };
  
  // Handle discount input changes
  const handleDiscountChange = (e) => {
    setDiscountString(e.target.value);
  };
  
  // Handle blur event - convert to number and validate (0-100%)
  const handleDiscountBlur = () => {
    const numValue = Number(discountString);
    if (isNaN(numValue) || numValue < 0) {
      setLocalDiscount(0);
      setDiscountString('0');
    } else if (numValue > 100) {
      setLocalDiscount(100);
      setDiscountString('100');
    } else {
      setLocalDiscount(numValue);
      setDiscountString(String(numValue));
    }
  }
  
  // Функція для застосування налаштувань
  const applySettings = () => {
    // Apply very custom product discount
    handleUpdateVeryCustomProductDiscount('ai_conversation', localDiscount);
    
    // Check if there are any actual custom settings
    const hasAnyRealCustomSettings = localDiscount > 0;
    
    onApply({
      nonEnglishAnalysis: false, // Always false now - unified pricing
      hasAiCustomSettings: hasAnyRealCustomSettings
    });
  };
  
  // Функція для скасування змін
  const cancelSettings = () => {
    onClose();
  };
  
  // Функція для скидання налаштувань до дефолтних (not needed now)
  const resetSettings = () => {
    // No settings to reset with unified pricing
  };
  
  return (
    <Modal 
      open={open} 
      onClose={cancelSettings}
      aria-labelledby="ai-settings-title"
    >
      <Box sx={{ 
        position: 'absolute', 
        top: '50%', 
        left: '50%', 
        transform: 'translate(-50%, -50%)', 
        width: { xs: '90%', sm: 500 }, 
        bgcolor: 'background.paper', 
        boxShadow: 24, 
        p: 4,
        borderRadius: theme.spacing(1), // Updated radius
        outline: 'none'
      }}>
        <Typography 
          id="ai-settings-title" 
          variant="h6" 
          component="h2" 
          sx={{ mb: 3, cursor: 'pointer', userSelect: 'none' }}
          onDoubleClick={handleTitleDoubleClick}
        >
          {t('calculator.aiSettingsTitle', 'AI Conversation Intelligence Settings')}
        </Typography>
        
        {/* Non-English Analysis - COMMENTED OUT FOR UNIFIED PRICING */}
        {/*
        <FormControlLabel 
          control={
            <Switch 
              checked={localNonEnglishAnalysis} 
              onChange={(e) => setLocalNonEnglishAnalysis(e.target.checked)} 
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ mr: 1 }}>{t('calculator.enableNonEnglishAnalysis', 'Enable Non-English Analysis')}</Typography>
              <Tooltip title={t('calculator.nonEnglishAnalysisTooltip', 'Unified pricing: $0.03 per minute for all regions (except Europe: €0.03)')}>
                <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </Tooltip>
            </Box>
          }
          sx={{ mb: 3 }}
        />
        */}
        
        {/* Pricing information removed - no settings needed for unified pricing */}
        
        {/* Advanced Settings */}
        {isAdvancedMode && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Accordion 
              defaultExpanded 
              elevation={0}
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: 1,
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  bgcolor: 'rgba(255, 152, 0, 0.08)',
                  '& .MuiAccordionSummary-content': { 
                    alignItems: 'center' 
                  }
                }}
              >
                <Typography variant="subtitle2" sx={{ color: '#FF9800', fontWeight: 600 }}>
                  {t('calculator.advancedSettings', 'Advanced Settings')}
                </Typography>
              </AccordionSummary>
              
              <AccordionDetails sx={{ pt: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('calculator.veryCustomProductDiscount', 'Custom Product Discount')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('calculator.customDiscountNote', 'Applied after bundle discounts (0-100%)')}
                  </Typography>
                  
                  <TextField
                    size="small"
                    type="number"
                    value={discountString}
                    onChange={handleDiscountChange}
                    onBlur={handleDiscountBlur}
                    InputProps={{
                      endAdornment: '%',
                      inputProps: { min: 0, max: 100, step: 1 }
                    }}
                    sx={{ width: 120 }}
                    helperText={localDiscount > 0 ? `${localDiscount}% discount applied` : 'No discount'}
                  />
                </Box>
                
                <Typography variant="caption" color="text.secondary">
                  {t('calculator.advancedSettingsHint', 'Access: Double-click title or Ctrl+Shift+C')}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </>
        )}
        
        {/* Кнопки дій */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          {/* Reset button removed - no settings to reset with unified pricing */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button onClick={cancelSettings} color="inherit">
              {t('calculator.cancel', 'Cancel')}
            </Button>
            <Button onClick={applySettings} variant="contained" sx={{ bgcolor: '#00C853', '&:hover': { bgcolor: '#00A040' } }}>
              {t('calculator.apply', 'Apply')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AISettingsModal;
